import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Disclaimer } from "../components/Disclaimer";
import { useAssumptions } from "../context/AssumptionsContext";
import { formatINRCompact } from "../lib/currency";
import { downloadCalculatorPDF, createScenario } from "../lib/api";
import { buildSchema, defaultValuesFor } from "./CalculatorRenderer";

/**
 * Generic Ready Reckoner renderer (Task 15.2) — a second calculator "shape" alongside
 * CalculatorRenderer's form+chart shape. A reckoner config declares `rowAxis`/`colAxis` (fixed
 * value lists + labels, e.g. investment amount x years), an optional small set of `extraFields`
 * that apply to every cell (e.g. an assumed return rate), and a pure `computeCell(rowValue,
 * colValue, extraValues)` function. This renders the extra-inputs form, the row x column table,
 * Disclaimer, Download-PDF (server renders the grid), and Save-scenario — the same behaviours
 * CLAUDE.md requires of every calculator, just with a table instead of a headline + chart.
 */
export function ReckonerRenderer({ config }) {
  const { assumptions } = useAssumptions();
  const extraFields = config.extraFields || [];
  const schema = useMemo(() => buildSchema(extraFields), [extraFields]);
  const [downloading, setDownloading] = useState(false);
  const [savingLabel, setSavingLabel] = useState(null);
  const [saving, setSaving] = useState(false);

  const {
    register,
    watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: defaultValuesFor({ fields: extraFields }, assumptions),
  });

  const extraValues = watch();
  const formatCell = config.formatCell || formatINRCompact;

  const grid = useMemo(() => {
    if (Object.keys(errors).length > 0) return null;
    try {
      return config.rowAxis.values.map((rowValue) =>
        config.colAxis.values.map((colValue) => config.computeCell(rowValue, colValue, extraValues))
      );
    } catch {
      return null;
    }
  }, [config, extraValues, errors]);

  async function handleDownloadPDF() {
    if (!grid) return;
    setDownloading(true);
    try {
      await downloadCalculatorPDF({
        calculator_title: config.title,
        inputs: extraFields.map((field) => {
          // Reuse the calculator formatter's percent-normalisation: 2 decimals + trailing %
          // so reckoner PDFs read the same way as single-calculator ones (issue #4 fix).
          if (field.type === "percent") {
            const n = Number(extraValues[field.key]);
            const value = Number.isFinite(n) ? `${n.toFixed(2)}%` : `${extraValues[field.key]}%`;
            return { label: field.label, value };
          }
          return { label: field.label, value: String(extraValues[field.key]) };
        }),
        table: {
          row_label: config.rowAxis.label,
          col_label: config.colAxis.label,
          row_headers: config.rowAxis.values.map((v) => (config.rowAxis.format ? config.rowAxis.format(v) : String(v))),
          col_headers: config.colAxis.values.map((v) => (config.colAxis.format ? config.colAxis.format(v) : String(v))),
          cells: grid.map((row) => row.map((cell) => formatCell(cell))),
        },
      });
    } catch {
      toast.error("Couldn't generate the PDF. Is the backend running?");
    } finally {
      setDownloading(false);
    }
  }

  async function handleConfirmSaveScenario() {
    if (!grid || !savingLabel.trim()) return;
    setSaving(true);
    try {
      await createScenario({
        calculator_key: config.key,
        label: savingLabel.trim(),
        inputs: extraValues,
        outputs: {},
        assumptions,
      });
      toast.success("Scenario saved — find it under My Scenarios.");
      setSavingLabel(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save this scenario.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>{config.title}</CardTitle>
          <CardDescription>{config.description}</CardDescription>
        </CardHeader>
        {extraFields.length > 0 && (
          <CardContent className="flex flex-wrap gap-4">
            {extraFields.map((field) => (
              <div key={field.key} className="flex w-40 flex-col gap-1.5">
                <Label htmlFor={field.key}>
                  {field.label}
                  {field.type === "percent" ? " (%)" : ""}
                </Label>
                <Input id={field.key} type="number" step="any" {...register(field.key)} />
                {errors[field.key] && <p className="text-xs text-red-400">{errors[field.key].message}</p>}
              </div>
            ))}
          </CardContent>
        )}
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Results</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {grid ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm" data-testid="reckoner-table">
                  <thead>
                    <tr>
                      <th className="border border-border p-2 text-left text-xs text-muted">
                        {config.rowAxis.label} \ {config.colAxis.label}
                      </th>
                      {config.colAxis.values.map((colValue) => (
                        <th key={`col-${colValue}`} className="border border-border p-2 text-right text-xs text-muted">
                          {config.colAxis.format ? config.colAxis.format(colValue) : colValue}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {config.rowAxis.values.map((rowValue, ri) => (
                      <tr key={`row-${rowValue}`}>
                        <td className="border border-border p-2 font-medium text-text">
                          {config.rowAxis.format ? config.rowAxis.format(rowValue) : rowValue}
                        </td>
                        {grid[ri].map((cell, ci) => (
                          <td key={`cell-${rowValue}-${config.colAxis.values[ci]}`} className="border border-border p-2 text-right text-text">
                            {formatCell(cell)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-2 flex gap-2">
                <Button variant="outline" onClick={handleDownloadPDF} disabled={downloading} className="flex-1">
                  {downloading ? "Generating…" : "Download PDF"}
                </Button>
                {savingLabel === null && (
                  <Button variant="outline" onClick={() => setSavingLabel(`${config.title} — ${new Date().toLocaleDateString()}`)} className="flex-1">
                    Save this scenario
                  </Button>
                )}
              </div>

              {savingLabel !== null && (
                <div className="flex flex-col gap-2 rounded-md border border-border p-3">
                  <Label htmlFor="scenario-label">Name this scenario</Label>
                  <div className="flex gap-2">
                    <Input id="scenario-label" value={savingLabel} onChange={(e) => setSavingLabel(e.target.value)} autoFocus />
                    <Button onClick={handleConfirmSaveScenario} disabled={saving || !savingLabel.trim()}>
                      {saving ? "Saving…" : "Confirm"}
                    </Button>
                    <Button variant="ghost" onClick={() => setSavingLabel(null)}>
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              <Disclaimer />
            </>
          ) : (
            <p className="text-sm text-muted">Enter valid values to see the table.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
